import React, { useEffect, useState } from "react";
import magazineCover from "../../assets/img/banner-sliders/3.avif";
import razorpayLogo from "../../assets/img/checkout/razorpay.jpg";
import { makeAPI } from "../../services/api";

const gateway = {
  id: "razorpay",
  name: "Razorpay",
  accentClass: "is-razorpay",
  logo: razorpayLogo,
  successRate: "96%"
};

const emptyBillingDetails = {
  fullName: "",
  phone: "",
  email: ""
};

function formatCurrency(value, currencyType = "INR") {
  const amount = Number(value || 0);

  if (!Number.isFinite(amount)) {
    return currencyType === "INR" ? "\u20B90" : `${currencyType} 0`;
  }

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currencyType || "INR",
      maximumFractionDigits: 0
    }).format(amount);
  } catch {
    return currencyType === "INR" ? `\u20B9${amount}` : `${currencyType} ${amount}`;
  }
}

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function validateBillingDetails(values) {
  const errors = {};
  const phone = String(values.phone || "").replace(/\D/g, "");
  const email = String(values.email || "").trim();

  if (!String(values.fullName || "").trim()) errors.fullName = "Full name is required.";
  if (!phone || phone.length < 10) errors.phone = "Enter a valid phone number.";
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email address.";

  return errors;
}

function loadRazorpayScript() {
  // Load Razorpay checkout lazily so the script is fetched only when a user actually pays.
  if (window.Razorpay) {
    return Promise.resolve(true);
  }

  const existingScript = document.querySelector('script[data-razorpay-checkout="true"]');
  if (existingScript) {
    return new Promise((resolve) => {
      existingScript.addEventListener("load", () => resolve(true), { once: true });
      existingScript.addEventListener("error", () => resolve(false), { once: true });
    });
  }

  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.razorpayCheckout = "true";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function CheckoutModal({ show, onClose, selectedPlan, onPurchaseSuccess }) {
  const [unitPrice, setUnitPrice] = useState(0);
  const [billingDetails, setBillingDetails] = useState(emptyBillingDetails);
  const [billingErrors, setBillingErrors] = useState({});
  const [visible, setVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const numericPrice = Number(selectedPlan?.amount || selectedPlan?.price || 0);
    setUnitPrice(Number.isNaN(numericPrice) ? 0 : numericPrice);
  }, [selectedPlan]);

  useEffect(() => {
    setVisible(Boolean(show));
    if (show) {
      const user = getStoredUser();
      setBillingErrors({});
      setBillingDetails({
        ...emptyBillingDetails,
        fullName: user?.name || "",
        phone: user?.phone_no || "",
        email: user?.email || ""
      });
    }
  }, [show]);

  if (!show) return null;

  const metaText = String(selectedPlan?.meta || "").toLowerCase();
  const purchaseType = metaText.includes("days access")
    ? "Subscription"
    : metaText.includes("lifetime access")
      ? "Lifetime Issue Access"
      : metaText.includes("free")
        ? "Free Issue"
        : "Issue Access";

  const totalPrice = unitPrice;

  const handleBillingChange = (event) => {
    const { name, value } = event.target;
    setBillingDetails((current) => ({
      ...current,
      [name]: value
    }));
    setBillingErrors((current) => ({
      ...current,
      [name]: ""
    }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateBillingDetails(billingDetails);
    if (Object.keys(validationErrors).length > 0) {
      setBillingErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);

      // Ask the server to create the real Razorpay entity first.
      // For issues this is an order, for plans this is a subscription.
      const orderResponse = await makeAPI("/api/purchase_magazine", "POST", true, {
        purchase_type: selectedPlan?.purchaseType,
        pricing_id: selectedPlan?.pricingId || null,
        issue_id: selectedPlan?.issueId || null,
        billing_details: billingDetails
      });

      if (orderResponse?.status !== "S") {
        if (orderResponse?.errors) {
          setBillingErrors(orderResponse.errors);
        }
        alert(orderResponse?.message || "Unable to start checkout.");
        return;
      }

      if (orderResponse?.data?.access_granted) {
        alert(orderResponse?.message || "Access is already active.");
        onPurchaseSuccess?.(orderResponse?.data || null);
        onClose?.();
        return;
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        alert("Unable to load Razorpay checkout right now.");
        return;
      }

      const razorpayOrder = orderResponse?.data?.razorpay_order;
      const razorpaySubscription = orderResponse?.data?.razorpay_subscription;
      const razorpayUser = orderResponse?.data?.user || {};
      const razorpayKeyId = orderResponse?.data?.key_id;
      const gatewayMode = orderResponse?.data?.gateway_mode || "order";

      if (
        !razorpayKeyId ||
        (gatewayMode === "subscription" && !razorpaySubscription?.id) ||
        (gatewayMode !== "subscription" && !razorpayOrder?.id)
      ) {
        alert("Unable to start Razorpay checkout right now.");
        return;
      }

      const options = {
        key: razorpayKeyId,
        ...(gatewayMode === "subscription"
          ? {
              // Subscription plans authenticate against a Razorpay subscription id.
              subscription_id: razorpaySubscription.id
            }
          : {
              // Single-issue checkout uses the normal Razorpay order flow.
              amount: razorpayOrder.amount,
              currency: razorpayOrder.currency,
              order_id: razorpayOrder.id
            }),
        // name: "CGES.CLUB",
        description: orderResponse?.data?.title || selectedPlan?.name || "Magazine Access",
        // image: gateway.logo,
        prefill: {
          name: razorpayUser.name || billingDetails.fullName,
          email: razorpayUser.email || billingDetails.email,
          contact: razorpayUser.contact || billingDetails.phone
        },
        notes: {
          purchase_type: selectedPlan?.purchaseType || "",
          item_name: selectedPlan?.name || ""
        },
        // theme: {
        //   color: "#ff810e"
        // },
        modal: {
          ondismiss: () => {
            setIsSubmitting(false);
          }
        },
        handler: async (paymentResult) => {
          try {
            setIsSubmitting(true);
            // Never trust the checkout success callback by itself.
            // We send the ids back to our server so it can verify the Razorpay signature securely.
            const verifyResponse = await makeAPI("/api/verify_magazine_payment", "POST", true, {
              razorpay_order_id: paymentResult?.razorpay_order_id,
              razorpay_subscription_id: paymentResult?.razorpay_subscription_id,
              razorpay_payment_id: paymentResult?.razorpay_payment_id,
              razorpay_signature: paymentResult?.razorpay_signature
            });

            if (verifyResponse?.status !== "S") {
              alert(verifyResponse?.message || "Payment verification failed.");
              return;
            }

            alert(verifyResponse?.message || "Purchase completed successfully.");
            onPurchaseSuccess?.(verifyResponse?.data || null);
            onClose?.();
          } catch {
            alert("Unable to verify payment right now.");
          } finally {
            setIsSubmitting(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on("payment.failed", () => {
        setIsSubmitting(false);
        alert("Payment was not completed. Please try again.");
      });
      razorpayInstance.open();
    } catch {
      alert("Unable to complete purchase right now.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="magazine-modal-backdrop" onClick={onClose} />
      <div className="magazine-modal-shell d-flex justify-content-center align-items-center">
        <div
          className={`magazine-modal checkout-modal ${visible ? "modal-visible" : ""}`}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            className="btn magazine-btn magazine-btn--ghost position-absolute top-2 end-2"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>

          <div className="checkout-modal__hero">
            <div>
              <span className="magazine-kicker">Secure Checkout</span>
              <h2 className="magazine-modal__title">Complete your purchase</h2>
              <p className="checkout-modal__hero-copy">
                {purchaseType === "Subscription"
                  ? "Start your magazine subscription with secure Razorpay checkout."
                  : "Unlock this issue with secure Razorpay checkout."}
              </p>
            </div>
            <div className="checkout-modal__hero-badge">Instant Access</div>
          </div>

          <div className="checkout-modal__grid">
            <section className="checkout-card checkout-card--summary">
              <div className="checkout-order-summary">
                <img src={magazineCover} alt="Magazine cover" />
                <div className="checkout-order-summary__body">
                  <span className="magazine-chip magazine-chip--accent">{purchaseType}</span>
                  <h3>CGES.CLUB Magazine</h3>
                  <p>{selectedPlan?.name || "Magazine Access"}</p>
                  <small>{selectedPlan?.meta || "Access details will appear here."}</small>
                </div>
              </div>

              <div className="checkout-price-stack">
                <div className="checkout-price-row">
                  <span>Plan price</span>
                  <strong>{formatCurrency(unitPrice, selectedPlan?.currencyType || "INR")}</strong>
                </div>
                <div className="checkout-price-row">
                  <span>Gateway</span>
                  <strong>{gateway.name}</strong>
                </div>
                <div className="checkout-price-row checkout-price-row--total">
                  <span>Total</span>
                  <strong>{formatCurrency(totalPrice, selectedPlan?.currencyType || "INR")}</strong>
                </div>
              </div>

              <div className="checkout-security-note">
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                <span>Orders are created on the server and access is granted only after Razorpay verification.</span>
              </div>
            </section>

            <section className="checkout-card checkout-card--form">
              <div className="checkout-block">
                <div className="checkout-block__title">Payment gateway</div>
                <div className="checkout-gateway-grid">
                  <button
                    type="button"
                    className={`checkout-gateway ${gateway.accentClass} is-active`}
                    disabled
                  >
                    <div className="checkout-gateway__logo">
                      <img src={gateway.logo} alt={`${gateway.name} logo`} />
                    </div>
                    <div className="checkout-gateway__body">
                      <strong>{gateway.name}</strong>
                      <small>{gateway.successRate} success</small>
                      <span>Cards, UPI, netbanking and wallets</span>
                    </div>
                  </button>
                </div>
              </div>

              <div className="checkout-block">
                <div className="checkout-block__title">Billing details</div>
                <div className="checkout-form-grid">
                  <label className="checkout-field">
                    <span>Full name</span>
                    <input
                      type="text"
                      name="fullName"
                      value={billingDetails.fullName}
                      onChange={handleBillingChange}
                      placeholder="Enter your full name"
                    />
                    {billingErrors.fullName ? <small>{billingErrors.fullName}</small> : null}
                  </label>
                  <label className="checkout-field">
                    <span>Phone</span>
                    <input
                      type="tel"
                      name="phone"
                      value={billingDetails.phone}
                      onChange={handleBillingChange}
                      placeholder="Enter your phone number"
                    />
                    {billingErrors.phone ? <small>{billingErrors.phone}</small> : null}
                  </label>
                  <label className="checkout-field checkout-field--full">
                    <span>Email</span>
                    <input
                      type="email"
                      name="email"
                      value={billingDetails.email}
                      onChange={handleBillingChange}
                      placeholder="Enter your email address"
                    />
                    {billingErrors.email ? <small>{billingErrors.email}</small> : null}
                  </label>
                </div>
              </div>

              <button
                type="button"
                className="btn magazine-btn magazine-btn--primary checkout-pay-btn"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : `Pay ${formatCurrency(totalPrice, selectedPlan?.currencyType || "INR")}`}
              </button>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
